/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-edit-custom-report/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import { ExecutionResult } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';

import { useBoolean, useTranslation } from 'hooks';
import {
    ReportDefinitionFields,
    ReportDefinitionAuditFields_ReportDefinition_tabs_ReportDefinitionTab as ReportDefinitionFields_tabs,
    ReportDefinitionAuditFields_ReportDefinition_tabs_ReportDefinitionTab_columns_ReportDefinitionColumn as ReportDefinitionFields_tabs_columns,
} from 'queries/generated/fragments.generated';
import { upsertReportDefinition } from 'queries/generated/report-definition.generated';
import {
    GET_REPORT_DEFINITION_OPTIONS,
    GET_REPORT_DEFINITIONS,
    UPSERT_REPORT_DEFINITION,
} from 'queries/report-definition';
import {
    ReportDefinitionColumnInput,
    ReportDefinitionFieldTypes,
    ReportDefinitionInput,
    ReportDefinitionTabInput,
    ReportDefinitionTabs,
} from 'types/graphql-global-types';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import objectEquals from 'utils/object-equals';
import { checkIfNameExist } from 'utils/validation';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogPage, DialogTitle } from 'views/components/core/dialog';
import Loading from 'views/components/core/loading';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import StepsBox from 'views/components/core/steps-box';
import Error from 'views/components/error/error';
import CustomReportStep1Name from 'views/components/reports/custom-report-config/add-edit-custom-report/step1-name';

import CustomReportStep2Data from 'views/components/reports/custom-report-config/add-edit-custom-report/step2-data';
import CustomReportStep3Columns from 'views/components/reports/custom-report-config/add-edit-custom-report/step3-columns';
import './styles.scss';
import {
    addColumnToTab,
    copyReportDefinition,
    copyTabFromTabArray,
    copyTabs,
    getTab,
    removeColumnFromTab,
    removeMatchingColumns,
    updateTabByTabType,
} from 'views/components/reports/custom-report-config/custom-report-helpers';

export enum CustomReportPage {
    step1Name = 1,
    step2Data = 2,
    step3Columns = 3,
}

export interface AddEditCustomReportErrors {
    [key: string]: string | null | undefined;
    name?: string;
    description?: string;
    tabs?: string;
}

interface AddEditCustomReportProps {
    open: boolean;
    onClose: () => void;
    reportDefinition?: ReportDefinitionFields;
    reportDefinitions: ReportDefinitionFields[];
    isUserReport: boolean;
    canViewInternalRisks: boolean;
}

export interface AddEditCustomReportState {
    reportDefinition: ReportDefinitionFields;
    optionsTabs: ReportDefinitionFields_tabs[];
}

const AddEditCustomReport = ({
    open,
    onClose,
    reportDefinition,
    reportDefinitions,
    isUserReport,
    canViewInternalRisks,
}: AddEditCustomReportProps) => {
    const emptyReportDefinition: ReportDefinitionFields = {
        __typename: 'ReportDefinition',
        id: Constants.ObjectId.Empty,
        userId: Constants.ObjectId.Empty,
        deleted: false,
        createdBy: null,
        updatedBy: null,
        created: null,
        updated: null,
        name: '',
        description: '',
        includeInternalRisks: false,
        includeNotApplicableRisks: false,
        includeDraftRisks: false,
        includeObsoleteRisks: false,
        recordId: null,
        tabs: [],
    };

    const initialState: AddEditCustomReportState = {
        reportDefinition: reportDefinition ? { ...reportDefinition } : { ...emptyReportDefinition },
        optionsTabs: reportDefinition?.tabs
            ? (reportDefinition?.tabs.filter((x) => x !== null) as ReportDefinitionFields_tabs[])
            : [],
    };

    const { t } = useTranslation();
    const step1NameRef = useRef<{ validationOnUpdate: () => boolean } | null>(null);
    const step2DataRef = useRef<{ validationOnUpdate: () => boolean } | null>(null);
    const disabledSave = useBoolean(false);
    const [page, setPage] = useState({
        number: CustomReportPage.step1Name,
        available: CustomReportPage.step1Name,
    } as DialogPage);
    const [errors, setErrors] = useState([] as AddEditCustomReportErrors[]);
    const [state, setState] = useState({ ...initialState } as AddEditCustomReportState);
    const [saveReportDefinition] = useMutation(UPSERT_REPORT_DEFINITION);
    const {
        loading: reportDefinitionOptionsLoading,
        error: reportDefinitionOptionsError,
        data: reportDefinitionOptionsData,
    } = useQuery(GET_REPORT_DEFINITION_OPTIONS);

    const showWarningLostData = useBoolean(false);
    const enabledClose = useBoolean(true);

    const reportDefinitionOptions = reportDefinitionOptionsData?.reportDefinitionOptions;

    const pageTitles = [
        t('reports:customReportPageStep1Name'),
        t('reports:customReportPageStep2Data'),
        t('reports:customReportPageStep3Columns'),
    ];

    const hasChanges = useBoolean(false);

    const errorStateHasErrors = (errorState: AddEditCustomReportErrors) =>
        errorState && Object.keys(errorState).filter((item: string) => errorState[item]).length > 0;

    const currentPageHasErrors = () => errorStateHasErrors(errors[page.number]);

    const anyPageHasErrors = () =>
        errors.some((errorState: AddEditCustomReportErrors) => errorStateHasErrors(errorState));

    const removeInternalRiskColumnFromTab = (tab: ReportDefinitionFields_tabs) =>
        removeColumnFromTab(tab, ReportDefinitionFieldTypes.RISK_INTERNAL);

    const addInternalRiskColumnToTab = (tab: ReportDefinitionFields_tabs) =>
        addColumnToTab(tab, ReportDefinitionFieldTypes.RISK_INTERNAL);

    const removeInternalRiskColumnByTabType = (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => {
        updateTabByTabType(tabs, tabType, removeInternalRiskColumnFromTab);
    };

    const addInternalRiskColumnByTabType = (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => {
        updateTabByTabType(tabs, tabType, addInternalRiskColumnToTab);
    };

    const applyFunctionToRiskInternalColumns = (
        updateFunction: (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => void,
        tabs: ReportDefinitionFields_tabs[],
    ) => {
        updateFunction(tabs, ReportDefinitionTabs.RISKS);
        updateFunction(tabs, ReportDefinitionTabs.RISKS_AND_CONTROLS);
        updateFunction(tabs, ReportDefinitionTabs.RISKS_AND_CRITICAL_VARIABLES);
    };

    const updateRiskInternalColumns = (include: boolean) => {
        const updateFunction: (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => void = include
            ? addInternalRiskColumnByTabType
            : removeInternalRiskColumnByTabType;

        const tabs = copyTabs(state.reportDefinition.tabs);

        applyFunctionToRiskInternalColumns(tabs, updateFunction);

        setState({
            ...state,
            reportDefinition: {
                ...state.reportDefinition,
                tabs: [...tabs],
                includeInternalRisks: include,
            },
        });
    };

    const removeRiskInternalColumnsFromOptionsTabs = () => {
        const tabs = copyTabs(state.optionsTabs);

        removeInternalRiskColumnByTabType(tabs, ReportDefinitionTabs.RISKS);
        removeInternalRiskColumnByTabType(tabs, ReportDefinitionTabs.RISKS_AND_CONTROLS);
        removeInternalRiskColumnByTabType(tabs, ReportDefinitionTabs.RISKS_AND_CRITICAL_VARIABLES);

        setState({
            ...state,
            optionsTabs: [...tabs],
        });
    };

    const getOptionsTabs = (reportDefinitionTabs: ReportDefinitionFields_tabs[]) => {
        const optionsTabs = copyTabs(reportDefinitionOptions?.tabs ?? []);

        removeMatchingColumns(optionsTabs, reportDefinitionTabs);

        return optionsTabs;
    };

    const getDefaultOptionsTab = (tabType: ReportDefinitionTabs) => {
        let tab = copyTabFromTabArray(reportDefinitionOptions.tabs, tabType);

        if (!state.reportDefinition.includeInternalRisks) tab = removeInternalRiskColumnFromTab(tab);

        return tab;
    };

    const reset = () => {
        if (reportDefinition && reportDefinitionOptions) {
            setState({
                reportDefinition: copyReportDefinition(reportDefinition),
                optionsTabs: getOptionsTabs(reportDefinition.tabs),
            });
        } else if (!reportDefinition && reportDefinitionOptions) {
            setState({
                ...initialState,
                optionsTabs: copyTabs(reportDefinitionOptions.tabs),
            });
        }
    };

    const resetTabs = () => {
        if (reportDefinition && reportDefinitionOptions)
            setState({
                reportDefinition: {
                    ...state.reportDefinition,
                    tabs: copyTabs(reportDefinition.tabs),
                },
                optionsTabs: getOptionsTabs(reportDefinition.tabs),
            });
        else if (!reportDefinition && reportDefinitionOptions) {
            setState({
                reportDefinition: {
                    ...state.reportDefinition,
                    tabs: [],
                },
                optionsTabs: copyTabs(reportDefinitionOptions.tabs),
            });
        }
    };

    const resetColumns = (tabType: ReportDefinitionTabs) => {
        const tabs = copyTabs(state.reportDefinition.tabs);
        const currentTab = getTab(tabs, tabType);
        const resetTab = reportDefinition ? getTab(reportDefinition.tabs, tabType) : getDefaultOptionsTab(tabType);

        currentTab.columns = [...resetTab.columns];

        setState({
            reportDefinition: {
                ...state.reportDefinition,
                tabs,
            },
            optionsTabs: getOptionsTabs(tabs),
        });
    };

    const onChangeStep = (pageNumber: number) => {
        if (page.number === CustomReportPage.step1Name && !step1NameRef.current?.validationOnUpdate()) return;

        if (page.number === CustomReportPage.step2Data && !step2DataRef.current?.validationOnUpdate()) return;

        setPage({ ...page, number: pageNumber });
    };

    const back = () => {
        setPage({ ...page, number: page.number - 1 });
    };

    const close = () => {
        setPage({ number: CustomReportPage.step1Name, available: CustomReportPage.step1Name });
        reset();
        setErrors([]);
        onClose();
    };

    const updateErrorState = (errorState: AddEditCustomReportErrors, pageNumber: number) => {
        const errorsState = [...errors];
        errorsState[pageNumber] = errorState;
        setErrors(errorsState);
    };

    const isNameUnique = () => !checkIfNameExist(state.reportDefinition, reportDefinitions, 'name');

    const getReportDefinitionInputColumns = (
        columns: ReportDefinitionFields_tabs_columns[],
    ): ReportDefinitionColumnInput[] =>
        columns.map(
            (column: ReportDefinitionFields_tabs_columns) =>
                ({
                    dataField: column.dataField,
                    index: 0,
                }) as ReportDefinitionColumnInput,
        );

    const getReportDefinitionInputTabs = (tabs: ReportDefinitionFields_tabs[]): ReportDefinitionTabInput[] =>
        tabs.map(
            (tab: ReportDefinitionFields_tabs) =>
                ({
                    tabType: tab.tabType,
                    columns: getReportDefinitionInputColumns(tab.columns),
                }) as ReportDefinitionTabInput,
        );

    const getReportDefinitionInput = (): ReportDefinitionInput => ({
        id: state.reportDefinition.id,
        userId: state.reportDefinition.userId,
        name: state.reportDefinition.name,
        description: state.reportDefinition.description,
        includeInternalRisks: state.reportDefinition.includeInternalRisks,
        includeNotApplicableRisks: state.reportDefinition.includeNotApplicableRisks,
        includeDraftRisks: state.reportDefinition.includeDraftRisks,
        includeObsoleteRisks: state.reportDefinition.includeObsoleteRisks,
        recordId: state.reportDefinition.recordId,
        tabs: getReportDefinitionInputTabs(state.reportDefinition.tabs),
    });

    const onSave = () => {
        if (
            anyPageHasErrors() ||
            !step1NameRef.current?.validationOnUpdate() ||
            !step2DataRef.current?.validationOnUpdate()
        ) {
            NotificationService.showNotification(t('reports:customReportSaveError'));
            return;
        }

        if (reportDefinition && !hasChanges.value) {
            NotificationService.showNotification(
                t('reports:customReportSaveUpdated', { id: reportDefinition.recordId || reportDefinition.id }),
            );
            close();
            return;
        }
        disabledSave.setTrue();
        saveReportDefinition({
            variables: {
                reportDefinition: getReportDefinitionInput(),
                isUserReport,
            },
            refetchQueries: [
                {
                    query: GET_REPORT_DEFINITIONS,
                },
            ],
        })
            .then((result: ExecutionResult<upsertReportDefinition>) => {
                disabledSave.setFalse();

                if (!result?.data?.upsertReportDefinition) {
                    NotificationService.showNotification(t('errorMessage'));
                    return;
                }

                const successMessage = reportDefinition
                    ? t('reports:customReportSaveUpdated', { id: reportDefinition.recordId || reportDefinition.id })
                    : t('reports:customReportSaveCreated');

                NotificationService.showNotification(successMessage);
                // onClose();
                close();
            })
            .catch((exception: any) => {
                NotificationService.showNotification(t('errorMessage'));
                disabledSave.setFalse();
                console.warn('Error saving report: ', exception);
            });
    };

    const handleNextButton = () => {
        if (page.number === CustomReportPage.step1Name && step1NameRef.current?.validationOnUpdate())
            setPage({ number: CustomReportPage.step2Data, available: CustomReportPage.step2Data });
        else if (page.number === CustomReportPage.step2Data && step2DataRef.current?.validationOnUpdate())
            setPage({ number: CustomReportPage.step3Columns, available: CustomReportPage.step3Columns });
        else if (page.number === CustomReportPage.step3Columns) onSave();
    };

    useEffect(() => {
        if (!state.reportDefinition.includeInternalRisks) removeRiskInternalColumnsFromOptionsTabs();
    }, [state.reportDefinition.tabs]);

    useEffect(() => {
        reset();
    }, [reportDefinition, reportDefinitionOptions]);

    useEffect(() => {
        if (open && reportDefinition)
            setPage({ number: CustomReportPage.step1Name, available: CustomReportPage.step3Columns });
    }, [open, reportDefinition]);

    useEffect(() => {
        if (currentPageHasErrors()) setPage({ ...page, available: page.number });
    }, [errors]);

    useEffect(() => {
        const initialStateTabs = getReportDefinitionInputTabs(initialState.reportDefinition.tabs);
        const stateTabs = getReportDefinitionInputTabs(state.reportDefinition.tabs);

        const initialStateTabsJson = JSON.stringify(initialStateTabs);
        const stateTabsJson = JSON.stringify(stateTabs);

        const fieldsChanged =
            initialState.reportDefinition.id !== state.reportDefinition.id ||
            initialState.reportDefinition.name !== state.reportDefinition.name ||
            initialState.reportDefinition.includeInternalRisks !== state.reportDefinition.includeInternalRisks ||
            initialState.reportDefinition.includeNotApplicableRisks !==
                state.reportDefinition.includeNotApplicableRisks ||
            initialState.reportDefinition.includeDraftRisks !== state.reportDefinition.includeDraftRisks ||
            initialState.reportDefinition.includeObsoleteRisks !== state.reportDefinition.includeObsoleteRisks ||
            initialState.reportDefinition.description !== state.reportDefinition.description;

        hasChanges.setValue(initialStateTabsJson !== stateTabsJson || fieldsChanged);
    }, [state]);

    const compareReportFields = (object1: any, object2: any) => {
        // check if changes in dialog were made
        const keys1 = Object.keys(object1);
        for (let i = 0; i < keys1.length; i++) {
            if (!objectEquals(object1[keys1[i]], object2[keys1[i]])) {
                return false;
            }
        }
        return true;
    };

    useEffect(() => {
        if (open && reportDefinition)
            enabledClose.setValue(compareReportFields(state.reportDefinition, reportDefinition));

        if (open && !reportDefinition)
            enabledClose.setValue(compareReportFields(state.reportDefinition, initialState.reportDefinition));
    }, [state.reportDefinition, reportDefinition, open]);

    if (reportDefinitionOptionsLoading) return <Loading />;

    if (reportDefinitionOptionsError) return <Error error={reportDefinitionOptionsError} />;

    return (
        <>
            <WarningLostDataDialog
                open={showWarningLostData.value}
                onClose={(action: string) => {
                    if (action === 'close') close();
                    showWarningLostData.setFalse();
                }}
            />
            <CustomDialog
                className="add-edit-custom-report max-w700"
                onClose={() => {}}
                fullWidth
                maxWidth="md"
                scroll="paper"
                open={open && !showWarningLostData.value}
            >
                <DialogTitle id="add-edit-custom-report-title">
                    {t('reports:addEditExcelListingConfig')}
                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={() => {
                            if (!enabledClose.value) showWarningLostData.setTrue();
                            else {
                                enabledClose.setTrue();
                                showWarningLostData.setFalse();
                                close();
                            }
                        }}
                        data-testid="close-dialog"
                    />
                </DialogTitle>
                <DialogContent className="add-edit-custom-report-content">
                    <StepsBox page={page} onChangeStep={onChangeStep} pageTitles={pageTitles} />
                    <CustomReportStep1Name
                        show={page.number === CustomReportPage.step1Name}
                        reportDefinition={state.reportDefinition}
                        state={state}
                        setState={setState}
                        updateErrorState={updateErrorState}
                        isNameUnique={isNameUnique}
                        canViewInternalRisks={canViewInternalRisks}
                        updateRiskInternalColumns={updateRiskInternalColumns}
                        ref={step1NameRef}
                    />
                    <CustomReportStep2Data
                        show={page.number === CustomReportPage.step2Data}
                        optionsTabs={state.optionsTabs}
                        reportDefinition={state.reportDefinition}
                        state={state}
                        setState={setState}
                        getDefaultOptionsTab={getDefaultOptionsTab}
                        reset={resetTabs}
                        updateErrorState={updateErrorState}
                        ref={step2DataRef}
                    />
                    <CustomReportStep3Columns
                        show={page.number === CustomReportPage.step3Columns}
                        optionsTabs={state.optionsTabs}
                        reportDefinition={state.reportDefinition}
                        state={state}
                        setState={setState}
                        resetColumns={resetColumns}
                    />
                    <DialogActions
                        className={`add-edit-custom-report-actions 
                        ${page.number === CustomReportPage.step1Name && !reportDefinition ? 'flex-end' : ''}`}
                    >
                        {page.number === CustomReportPage.step1Name && reportDefinition && (
                            <ModalUpdatedInfo
                                item={{ updated: reportDefinition.updated, created: reportDefinition.created }}
                            />
                        )}
                        {page.number > CustomReportPage.step1Name && (
                            <Button
                                text={page.number === CustomReportPage.step3Columns ? 'backToData' : 'backToName'}
                                id="back-button"
                                onClick={back}
                            />
                        )}
                        <div>
                            {page.number < CustomReportPage.step3Columns &&
                                page.available === CustomReportPage.step3Columns && (
                                    <Button
                                        disabled={disabledSave.value || currentPageHasErrors()}
                                        text="save"
                                        id="save-button"
                                        className="save-button"
                                        onClick={onSave}
                                    />
                                )}

                            <Button
                                disabled={disabledSave.value || currentPageHasErrors()}
                                text={page.number < CustomReportPage.step3Columns ? 'nextButton' : 'save'}
                                id={page.number < CustomReportPage.step3Columns ? 'next-button' : 'save-button'}
                                className="save-button-final-page"
                                onClick={handleNextButton}
                            />
                        </div>
                    </DialogActions>
                </DialogContent>
            </CustomDialog>
        </>
    );
};

export default AddEditCustomReport;
